import { Navigate, createBrowserRouter } from "react-router-dom";

import { AdminLayout } from "@/components/admin/AdminLayout";
import { AuthGuard } from "@/components/AuthGuard";
import { DashboardLanding } from "@/components/DashboardLanding";
import { PermessoGuard } from "@/components/PermessoGuard";
import { RootLayout } from "@/components/RootLayout";
import { AdminAgentePage } from "@/pages/AdminAgentePage";
import { AdminAgentiPage } from "@/pages/AdminAgentiPage";
import { AdminAiOpsPage } from "@/pages/AdminAiOpsPage";
import { AdminAuditPage } from "@/pages/AdminAuditPage";
import { AdminBachecaPage } from "@/pages/AdminBachecaPage";
import { AdminConversazioniPage } from "@/pages/AdminConversazioniPage";
import { AdminDocumentiPage } from "@/pages/AdminDocumentiPage";
import { AdminEconomiaFatturePage } from "@/pages/AdminEconomiaFatturePage";
import { AdminEconomiaScadenzePage } from "@/pages/AdminEconomiaScadenzePage";
import { AdminFeedPage } from "@/pages/AdminFeedPage";
import { AdminGanttPage } from "@/pages/AdminGanttPage";
import { AdminIncontriPage } from "@/pages/AdminIncontriPage";
import { AdminLogsPage } from "@/pages/AdminLogsPage";
import { AdminModuliPage } from "@/pages/AdminModuliPage";
import { AdminPage } from "@/pages/AdminPage";
import { AdminRuoliPage } from "@/pages/AdminRuoliPage";
import { AdminRuoloPermessiPage } from "@/pages/AdminRuoloPermessiPage";
import { AdminSettimanaPage } from "@/pages/AdminSettimanaPage";
import { AdminSistemaPage } from "@/pages/AdminSistemaPage";
import { AdminUtentePermessiPage } from "@/pages/AdminUtentePermessiPage";
import { AdminUtentiPage } from "@/pages/AdminUtentiPage";
import { AttivitaPage } from "@/pages/AttivitaPage";
import { BachecaPage } from "@/pages/BachecaPage";
import { CalendarioPage } from "@/pages/CalendarioPage";
import { ConversazioniPage } from "@/pages/ConversazioniPage";
import { DocumentiPage } from "@/pages/DocumentiPage";
import { GanttPage } from "@/pages/GanttPage";
import { HomePage } from "@/pages/HomePage";
import { LoginPage } from "@/pages/LoginPage";
import { M1OrderReadinessPage } from "@/pages/M1OrderReadinessPage";
import { M1UploadPage } from "@/pages/M1UploadPage";
import { M2PianificazionePage } from "@/pages/M2PianificazionePage";
import { M3FormDatiPage } from "@/pages/M3FormDatiPage";
import { M3SpedizioniPage } from "@/pages/M3SpedizioniPage";
import { M4DirezionePage } from "@/pages/M4DirezionePage";
import { PartenzePage } from "@/pages/PartenzePage";
import { MemoriaPage } from "@/pages/MemoriaPage";
import { MemoriaTipiPage } from "@/pages/MemoriaTipiPage";
import { ModuliPage } from "@/pages/ModuliPage";
import { ModuloDettaglioPage } from "@/pages/ModuloDettaglioPage";
import { NonDisponibilePage } from "@/pages/NonDisponibilePage";
import { NotFoundPage } from "@/pages/NotFoundPage";

export const router = createBrowserRouter([
  // Pagina di login pubblica (accessibile anche senza autenticazione).
  { path: "/login", element: <LoginPage /> },

  // Form «dati mancanti» del carico: pubblico via magic-link token monouso
  // (D7, agente documenti M3) — la difesa è il token, non la sessione.
  { path: "/m3/carico/:caricoId/dati", element: <M3FormDatiPage /> },

  // Tutto il resto è dietro AuthGuard → redirect a /login se non autenticato.
  {
    element: <AuthGuard />,
    children: [
      {
        path: "/",
        element: <RootLayout />,
        children: [
          // Landing operativa (SPEC-A §3): `/` = dashboard; cliente/viewer
          // reindirizzati a /non-disponibile da DashboardLanding.
          { index: true, element: <DashboardLanding /> },
          // Compat: vecchio bookmark /dashboard → landing.
          { path: "dashboard", element: <Navigate to="/" replace /> },
          { path: "non-disponibile", element: <NonDisponibilePage /> },
          { path: "memoria", element: <MemoriaPage /> },
          { path: "memoria/tipi", element: <MemoriaTipiPage /> },
          // Vetrina di progetto (ex landing): ora a /progetto, gated (staff + direzione).
          {
            element: <PermessoGuard tipo="nav_voce" permessoKey="progetto" />,
            children: [
              { path: "progetto", element: <HomePage /> },
              // La timeline a piena pagina: ci si arriva cliccando il Gantt.
              { path: "progetto/gantt", element: <GanttPage /> },
            ],
          },
          {
            element: <PermessoGuard tipo="nav_voce" permessoKey="bacheca" />,
            children: [{ path: "bacheca", element: <BachecaPage /> }],
          },
          {
            element: <PermessoGuard tipo="nav_voce" permessoKey="documenti" />,
            children: [{ path: "documenti", element: <DocumentiPage /> }],
          },
          {
            element: <PermessoGuard tipo="nav_voce" permessoKey="conversazioni" />,
            children: [{ path: "conversazioni", element: <ConversazioniPage /> }],
          },
          {
            element: <PermessoGuard tipo="nav_voce" permessoKey="attivita" />,
            children: [{ path: "attivita", element: <AttivitaPage /> }],
          },
          // I moduli operativi NON stanno più sotto il permesso `nav_voce.moduli`.
          // Quello governa la **vetrina** dell'avanzamento per il cliente: finché
          // i moduli ci stavano sotto, per entrare in M3 serviva anche il permesso
          // di vedere la vetrina. Ora ogni modulo è governato solo dal suo
          // `modulo.{codice}`, che è anche ciò che filtra la voce nella barra.
          {
            element: <PermessoGuard tipo="modulo" permessoKey="M1" />,
            children: [
              { path: "moduli/order-readiness", element: <M1OrderReadinessPage /> },
            ],
          },
          {
            element: <PermessoGuard tipo="modulo" permessoKey="M2" />,
            children: [
              { path: "moduli/pianificazione", element: <M2PianificazionePage /> },
            ],
          },
          // M4 «Direzione»: dal 18-08 il modulo del presidente ha una pagina, e
          // `modulo.M4` — che prima non governava niente — governa questa rotta.
          {
            element: <PermessoGuard tipo="modulo" permessoKey="M4" />,
            children: [{ path: "moduli/direzione", element: <M4DirezionePage /> }],
          },
          {
            element: <PermessoGuard tipo="modulo" permessoKey="M3" />,
            children: [
              { path: "moduli/spedizioni", element: <M3SpedizioniPage /> },
              // Il tabellone delle partenze: lo guarda il presidente, quindi vive
              // su una rotta corta e memorizzabile, ma nella barra è una sezione
              // di Spedizioni (decisione di Enrico, 12-08). La sezione eredita da
              // M3: senza un «No» esplicito vale quanto il modulo.
              {
                element: (
                  <PermessoGuard
                    tipo="nav_voce"
                    permessoKey="m3.partenze"
                    ereditaDa="M3"
                    redirectTo="/moduli/spedizioni"
                  />
                ),
                children: [{ path: "partenze", element: <PartenzePage /> }],
              },
              // Il calendario dei ritiri: la pagina di Marina, e il suo
              // atterraggio. Sezione di M3 come le altre — eredita dal modulo.
              {
                element: (
                  <PermessoGuard
                    tipo="nav_voce"
                    permessoKey="m3.calendario"
                    ereditaDa="M3"
                    redirectTo="/moduli/spedizioni"
                  />
                ),
                children: [{ path: "calendario", element: <CalendarioPage /> }],
              },
            ],
          },
          {
            element: <PermessoGuard tipo="nav_voce" permessoKey="moduli" />,
            children: [
              { path: "moduli", element: <ModuliPage /> },
              { path: "moduli/:slug", element: <ModuloDettaglioPage /> },
            ],
          },
          {
            element: <AdminLayout />,
            children: [
              { path: "admin", element: <AdminPage /> },
              { path: "admin/bacheca", element: <AdminBachecaPage /> },
              { path: "admin/bacheca/:voceId", element: <AdminBachecaPage /> },
              { path: "admin/utenti", element: <AdminUtentiPage /> },
              { path: "admin/utenti/:email/permessi", element: <AdminUtentePermessiPage /> },
              { path: "admin/ruoli", element: <AdminRuoliPage /> },
              { path: "admin/ruoli/:ruolo/permessi", element: <AdminRuoloPermessiPage /> },
              { path: "admin/moduli", element: <AdminModuliPage /> },
              { path: "admin/gantt", element: <AdminGanttPage /> },
              { path: "admin/documenti", element: <AdminDocumentiPage /> },
              { path: "admin/feed", element: <AdminFeedPage /> },
              // La Panoramica del cliente si governa da queste due: il KPI
              // «Prossimo incontro» e la casella «Questa settimana».
              { path: "admin/incontri", element: <AdminIncontriPage /> },
              { path: "admin/settimana", element: <AdminSettimanaPage /> },
              { path: "admin/agenti", element: <AdminAgentiPage /> },
              { path: "admin/agenti/:slug", element: <AdminAgentePage /> },
              { path: "admin/economia/scadenze", element: <AdminEconomiaScadenzePage /> },
              { path: "admin/economia/fatture", element: <AdminEconomiaFatturePage /> },
              { path: "admin/sistema", element: <AdminSistemaPage /> },
              { path: "admin/logs", element: <AdminLogsPage /> },
              { path: "admin/ai-ops", element: <AdminAiOpsPage /> },
              {
                path: "admin/agente-chat",
                element: (
                  <Navigate to="/admin/agenti/aida?tab=conversazioni&canale=chat" replace />
                ),
              },
              { path: "admin/audit", element: <AdminAuditPage /> },
              { path: "admin/conversazioni", element: <AdminConversazioniPage /> },
              { path: "admin/m1/upload", element: <M1UploadPage /> },
              { path: "admin/*", element: <AdminPage /> },
            ],
          },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
